import { Link } from 'react-router-dom';
import { storeCopy } from '@/content/store-copy';
import { CATEGORY_LABEL, PRODUCT_CATEGORIES, type Product } from '@/features/products/types';
import { imageSrc } from '@/lib/images';

/**
 * Three collection cards. Each one borrows the cover photo of its first
 * product, so adding a product to a collection updates the card for free.
 */
export function CollectionStrip({ products }: { products: Product[] }) {
  const cards = PRODUCT_CATEGORIES.filter((category) => category !== 'other')
    .map((category) => ({
      category,
      label: CATEGORY_LABEL[category],
      cover: products.find((product) => product.category === category)?.images[0] ?? null,
      count: products.filter((product) => product.category === category).length,
    }))
    .filter((card) => card.count > 0);

  if (cards.length === 0) return null;

  return (
    <section
      aria-labelledby="collections-title"
      className="bg-night px-[clamp(20px,4vw,64px)] py-[64px] max-[860px]:py-12 max-[560px]:px-4 max-[560px]:py-10"
    >
      <div className="mx-auto max-w-[1420px]">
        <p className="text-[0.78rem] tracking-[0.22em] text-brand-muted uppercase">
          {storeCopy.collections.label}
        </p>
        <h2
          id="collections-title"
          className="mt-[5px] font-serif text-[clamp(2rem,3.4vw,2.9rem)] leading-none"
        >
          {storeCopy.collections.title}
        </h2>

        <div className="mt-8 grid grid-cols-3 gap-[18px] max-[860px]:grid-cols-1 max-[860px]:gap-3">
          {cards.map((card) => (
            <Link
              key={card.category}
              to={`/shop?category=${card.category}`}
              className="group relative block aspect-[3/4] overflow-hidden border border-white/10 bg-panel max-[860px]:aspect-[16/9]"
            >
              {card.cover ? (
                <img
                  src={imageSrc(card.cover)}
                  alt=""
                  width={600}
                  height={800}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 size-full object-cover saturate-[0.85] transition-transform duration-500 group-hover:scale-[1.04]"
                />
              ) : (
                <div className="absolute inset-0 bg-[#111]" />
              )}

              <div
                aria-hidden
                className="absolute inset-0 bg-[linear-gradient(180deg,transparent_35%,rgba(5,5,6,0.86)_100%)]"
              />

              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="font-serif text-[1.6rem] leading-none text-cream">{card.label}</p>
                <p className="mt-1.5 text-[0.8rem] text-brand-muted">
                  {storeCopy.collections.action(card.label)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
